import { useCallback, useEffect, useRef, useState } from 'react';

// Las PCs de las cajas dejan el home abierto durante días o meses sin recargar, y cada
// deploy del front les llegaba recién cuando alguien recargaba la página. Este hook mira
// cada 5 minutos qué bundles publica /index.html (Hosting lo sirve con no-cache) y, si no
// son los que están corriendo, recarga la página cuando no interrumpe a nadie. Mientras
// tanto el home muestra un aviso para actualizar en el momento (ver README, "Versión nueva
// del front").

const INTERVALO_CHEQUEO = 5 * 60 * 1000;
// Con una versión nueva esperando, cada cuánto se vuelve a ver si ya se puede recargar.
// Es local: no hace pedidos.
const INTERVALO_ESPERA = 15 * 1000;
const SIN_ACTIVIDAD = 60 * 1000;
// Si una recarga no trajo la versión nueva (por ejemplo, un borde del CDN que todavía
// sirve el index.html viejo), no se vuelve a recargar por esa versión hasta pasada una hora.
const ESPERA_REINTENTO = 60 * 60 * 1000;
const CLAVE_RECARGA = 'recarga_version_nueva';

// Los bundles de entrada de CRA: /static/js/main.<hash>.js y /static/css/main.<hash>.css.
// Los chunks que se cargan después se llaman <n>.<hash>.chunk.js y no entran.
const PATRON_BUNDLE = /\/static\/(?:js|css)\/main\.[0-9a-f]+\.(?:js|css)/g;

const firmaDe = (texto) => [...new Set(texto.match(PATRON_BUNDLE) || [])].sort().join(' ');

// La versión que corre: los bundles que cargó esta página.
const firmaCorriendo = () => firmaDe(
    [...document.querySelectorAll('script[src], link[rel="stylesheet"][href]')]
        .map(elemento => elemento.getAttribute('src') || elemento.getAttribute('href'))
        .join(' ')
);

// La versión publicada. Falla si no se pudo leer o si la respuesta no trae bundles (una
// página de error, por ejemplo): en ese caso no se recarga nada.
const firmaPublicada = async () => {
    const respuesta = await fetch(`${process.env.PUBLIC_URL}/index.html`, { cache: 'no-store' });
    if (!respuesta.ok) throw new Error(`index.html respondió ${respuesta.status}`);
    const firma = firmaDe(await respuesta.text());
    if (!firma) throw new Error('index.html sin bundles');
    return firma;
};

// La última recarga por versión nueva de esta pestaña: { firma, en }.
const leerRecarga = () => {
    try {
        return JSON.parse(sessionStorage.getItem(CLAVE_RECARGA)) || null;
    } catch (error) {
        return null;
    }
};

const guardarRecarga = (recarga) => {
    try {
        if (recarga) {
            sessionStorage.setItem(CLAVE_RECARGA, JSON.stringify(recarga));
        } else {
            sessionStorage.removeItem(CLAVE_RECARGA);
        }
    } catch (error) {
        // Sin sessionStorage no hay freno por recargas fallidas: queda el de una cada 5 minutos.
    }
};

const recargarHacia = (firma) => {
    guardarRecarga({ firma, en: Date.now() });
    window.location.reload();
};

// `ocupado()` dice si la página está en medio de algo que una recarga interrumpiría. Además,
// nunca se recarga sola con actividad en el último minuto: teclado, mouse o un cambio de
// pestaña. Devuelve `hayVersionNueva`, para mostrar un aviso mientras espera, y `recargar`,
// para que el aviso recargue en el momento.
export function useRecargaConVersionNueva(ocupado) {
    const ocupadoRef = useRef(ocupado);
    ocupadoRef.current = ocupado;
    const [nueva, setNueva] = useState(null);

    useEffect(() => {
        const actual = firmaCorriendo();
        // Con `npm start` el bundle no tiene hash: no hay con qué comparar.
        if (!actual) return undefined;

        const anterior = leerRecarga();
        if (anterior?.firma === actual) guardarRecarga(null);
        const enEspera = (firma) =>
            firma === anterior?.firma && Date.now() - anterior.en < ESPERA_REINTENTO;

        let activo = true;
        let timer = null;
        let ultimaActividad = Date.now();
        const marcarActividad = () => {
            ultimaActividad = Date.now();
        };
        const eventos = ['pointerdown', 'keydown', 'wheel', 'visibilitychange'];
        eventos.forEach(evento => document.addEventListener(evento, marcarActividad, true));

        const intentarRecargar = (firma) => {
            if (Date.now() - ultimaActividad >= SIN_ACTIVIDAD && !ocupadoRef.current()) {
                recargarHacia(firma);
                return;
            }
            timer = setTimeout(() => intentarRecargar(firma), INTERVALO_ESPERA);
        };

        const chequear = async () => {
            try {
                const publicada = await firmaPublicada();
                if (!activo) return;
                if (publicada !== actual && !enEspera(publicada)) {
                    console.log('Hay una versión nueva del front: se recarga cuando no interrumpa');
                    setNueva(publicada);
                    intentarRecargar(publicada);
                    return;
                }
            } catch (error) {
                if (!activo) return;
                console.warn('No se pudo ver si hay una versión nueva del front:', error.message);
            }
            timer = setTimeout(chequear, INTERVALO_CHEQUEO);
        };

        // El primero, en un momento al azar: así, después de un deploy, las cajas no
        // recargan todas juntas.
        timer = setTimeout(chequear, Math.random() * INTERVALO_CHEQUEO);

        return () => {
            activo = false;
            clearTimeout(timer);
            eventos.forEach(evento => document.removeEventListener(evento, marcarActividad, true));
        };
    }, []);

    const recargar = useCallback(() => recargarHacia(nueva), [nueva]);
    return { hayVersionNueva: nueva !== null, recargar };
}
